import React, { useCallback, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
	ChildRows,
	type Props as ChildRowsProps,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows/index.tsx';
import { ChildRowsLoader } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows-loader/ChildRowsPreloadedQuery.tsx';
import type {
	RenderChildRowsArgs,
	RenderCreateIssueController,
	RenderDraggableWrapper,
	RenderDraggableWrapperProps,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';
import type { RenderTableCell } from '@atlassian/jira-issue-table-hierarchy/src/ui/loading-row/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { RenderChildRowsComp_rank$key } from '@atlassian/jira-relay/src/__generated__/RenderChildRowsComp_rank.graphql';
import type { RenderChildRowsComp$key } from '@atlassian/jira-relay/src/__generated__/RenderChildRowsComp.graphql';
import { Rows } from '../../../rows/Rows.tsx';
import {
	CHILD_ITEMS_EXPERIENCE,
	CHILD_ITEMS_LOAD_MORE_EXPERIENCE,
	ANALYTICS_SOURCE,
	MAX_COLUMNS,
	ACTION_COLUMNS,
	CHILD_QUERY_PAGE_SIZE,
} from '../../../../common/constants.tsx';
import {
	useIsReparentingEnabled,
	useSortDirection,
	useSortField,
	useJql,
	useIsFixedTableLayoutEnabled,
	useIsIssueRankDescending,
	useIsHideDoneItemsEnabled,
	useIsTimelineEnabled,
	useIsInlineFieldConfigEnabled,
	useIsConditionalFormattingEnabled,
	useIsRelationshipFieldEnabled,
	useCellBorderStyle,
	useIsRemoteLinkedIssuesEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { TableCell } from '../../../../common/ui/table-cell/index.tsx';
import { useScrollStateSelector } from '../../../../controllers/scroll-state/index.tsx';
import { CreateIssueController } from '../../../../services/issue-create-mutation/CreateIssueController.tsx';
import { DraggableRowsContainer } from '../../../../controllers/draggable-rows/context.tsx';
import type { Column, RankFieldTerms } from '../../../../common/types.tsx';
import { useTimelineCustomComponents } from '../../../../controllers/timeline-custom-components/index.tsx';

const renderDraggableWrapper: RenderDraggableWrapper = (props: RenderDraggableWrapperProps) => {
	return (
		<DraggableRowsContainer
			issueConnectionId={props.issueConnectionId}
			instanceId={props.instanceId}
		>
			{props.children}
		</DraggableRowsContainer>
	);
};

export const RenderChildRowsComp = ({
	project,
	projectKey,
	isGroupingSupported,
	columns,
	rankField,
	isStickyColumnsVisible,
	...childRowsProps
}: RenderChildRowsArgs & {
	projectKey?: string;
	isStickyColumnsVisible?: boolean;
	isGroupingSupported?: boolean;
	columns: Column[];
	project: RenderChildRowsComp$key | null | undefined;
	rankField: RenderChildRowsComp_rank$key | null;
}) => {
	let ChildIssueCreateRowComponent = null;
	if (fg('jira_native_issue_table_extra_custom_components')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		ChildIssueCreateRowComponent = useTimelineCustomComponents()?.ChildIssueCreateRow;
	}

	const renderTableCell: RenderTableCell = (tableCellProps) => <TableCell {...tableCellProps} />;

	const projectRef = useFragment<RenderChildRowsComp$key>(
		graphql`
			fragment RenderChildRowsComp on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				...Rows_project @arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project,
	);

	const rankFieldData = useFragment<RenderChildRowsComp_rank$key>(
		graphql`
			fragment RenderChildRowsComp_rank on JiraJqlFieldWithAliases
			@argumentDefinitions(
				isJSCAliasChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-alias-changes.relayprovider"
				}
			) {
				fieldId
				jqlTerm
				aliases @include(if: $isJSCAliasChangesEnabled)
			}
		`,
		rankField,
	);
	/**
	 * Passing those values down to ChildRowsLoader as props to avoid direct imports
	 */
	const jql = useJql();
	const sortField = useSortField();
	const sortDirection = useSortDirection();
	const isReparentingEnabled = useIsReparentingEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();
	const isHideDoneItemsEnabled = useIsHideDoneItemsEnabled();
	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	const width = useScrollStateSelector((scrollState) => scrollState.width);
	const isIssueRankDescending = useIsIssueRankDescending();
	const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();
	const isLinkRelationshipFieldEnabled = useIsRelationshipFieldEnabled();
	const isRemoteLinkedIssuesEnabled = useIsRemoteLinkedIssuesEnabled();
	const cellBorderStyle = useCellBorderStyle();

	const isConditionalFormattingEnabled =
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		fg('jira_list_conditional_formatting') && useIsConditionalFormattingEnabled();

	const rankFieldTerms = useMemo<RankFieldTerms>(
		() => ({
			jqlTerm: rankFieldData?.jqlTerm ?? null,
			aliases: rankFieldData?.aliases ? Array.from(rankFieldData.aliases) : null,
		}),
		[rankFieldData],
	);

	const renderCreateIssueController = useCallback<RenderCreateIssueController>(
		(props) => <CreateIssueController {...props} jql={jql} rankFieldTerms={rankFieldTerms} />,
		[jql, rankFieldTerms],
	);

	const renderChildRowsLoader = useCallback<ChildRowsProps['renderChildRowsLoader']>(
		(childRowsLoaderProps) => (
			<ChildRowsLoader
				{...childRowsLoaderProps}
				jql={jql}
				width={width}
				sortField={sortField}
				sortDirection={sortDirection}
				columns={columns}
				amountOfColumns={MAX_COLUMNS}
				actionColumns={ACTION_COLUMNS}
				pageSize={CHILD_QUERY_PAGE_SIZE}
				isReparentingEnabled={isReparentingEnabled}
				isTimelineEnabled={isTimelineEnabled}
				isIssueRankDescending={isIssueRankDescending}
				isHideDoneItemsEnabled={isHideDoneItemsEnabled}
				isFixedTableLayoutEnabled={isFixedTableLayoutEnabled}
				isConditionalFormattingEnabled={isConditionalFormattingEnabled}
				rankFieldId={rankFieldData?.fieldId}
				renderRows={(rowProps) => <Rows project={projectRef} {...rowProps} />}
				renderDraggableWrapper={renderDraggableWrapper}
				renderTableCell={renderTableCell}
				isStickyColumnsVisible={isStickyColumnsVisible}
				renderCreateIssueController={renderCreateIssueController}
				isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
				ChildIssueCreateRowComponent={ChildIssueCreateRowComponent}
				isLinkRelationshipFieldEnabled={isLinkRelationshipFieldEnabled}
				isRemoteLinkedIssuesEnabled={isRemoteLinkedIssuesEnabled}
				cellBorderStyle={cellBorderStyle}
			/>
		),
		[
			jql,
			width,
			sortField,
			sortDirection,
			columns,
			isReparentingEnabled,
			isTimelineEnabled,
			isIssueRankDescending,
			isHideDoneItemsEnabled,
			isFixedTableLayoutEnabled,
			isConditionalFormattingEnabled,
			rankFieldData?.fieldId,
			isStickyColumnsVisible,
			renderCreateIssueController,
			isInlineFieldConfigEnabled,
			cellBorderStyle,
			projectRef,
			ChildIssueCreateRowComponent,
			isLinkRelationshipFieldEnabled,
			isRemoteLinkedIssuesEnabled,
		],
	);

	return (
		<ChildRows
			{...childRowsProps}
			projectKey={projectKey}
			isGroupingSupported={isGroupingSupported}
			childItemsExperience={CHILD_ITEMS_EXPERIENCE}
			childItemsLoadMoreExperience={CHILD_ITEMS_LOAD_MORE_EXPERIENCE}
			analyticsSource={ANALYTICS_SOURCE}
			renderChildRowsLoader={renderChildRowsLoader}
		/>
	);
};
